import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { magpCMSApi } from "~/Services";

interface CameraAccessPopUpResponse {
  id: number;
  name: string;
  field_access_permission_ms: string;
  field_access_permission_zh: string;
  field_allow_btn_en: string;
  field_allow_btn_ms: string;
  field_allow_btn_zh: string;
  field_cancel_btn_en: string;
  field_cancel_btn_ms: string;
  field_cancel_btn_zh: string;
  field_confirm_text_en: string;
  field_confirm_text_ms: string;
  field_confirm_text_zh: string;
}

interface LanguagePopUpResponse {
  id: number;
  field_language_en: string;
  field_language_ms: string;
  field_language_zh: string;
  name: string;
  field_name_ms: string;
  field_name_zh: string;
  field_bhasha_en: string;
  field_bhasha_ms: string;
  field_bhasha_zh: string;
  field_mandrain_en: string;
  field_mandrain_ms: string;
  field_mandrain_zh: string;
  field_continue_en: string;
  field_continue_ms: string;
  field_continue_zh: string;
}

interface PurchaseLoginPopUpResponse {
  id: number;
  field_purchase_login_pop_image: string;
  name: string;
  field_purchase_text_ms: string;
  field_purchase_text_zh: string;
  field_purchase_login_pop_button: string;
  field_purchase_login_button_ms: string;
  field_purchase_login_button_zh: string;
}

interface LogoutPopUpResponse {
  id: number;
  field_logout_image: string;
  name: string;
  field_logout_txt_ms: string;
  field_logout_txt_zh: string;
  field_no_text_ms: string;
  field_no_text_en: string;
  field_no_text_zh: string;
  field_yes_confirm_text_en: string;
  field_yes_confirm_txt_ms: string;
  field_yes_confirm_txt_zh: string;
}

interface LoginPageResponse {
  id: number;
  field_login_image: string;
  field_login_text: string;
  field_login_text_zh: string;
  field_login_text_ms: string;
  field_login_email_text: string;
  field_login_email_text_ms: string;
  field_login_email_text_zh: string;
  field_login_password: string;
  field_login_password_ms: string;
  field_login_password_zh: string;
  field_login_button_text: string;
  field_login_button_text_ms: string;
  field_login_button_text_zh: string;
  field_forgot_password: string;
  field_forgot_password_text_ms: string;
  field_forgot_password_text_zn: string;
  showImage: string;
  field_google_text: string;
  field_google_text_ms: string;
  field_google_text_zh: string;
  field_facebook_text: string;
  field_facebook_text_ms: string;
  field_facebook_text_zh: string;
  fbImage: string;
  googleImage: string;
  field_login_or_text: string;
  field_login_or_text_ms: string;
  field_login_or_text_zh: string;
  field_register_now_text_en: string;
  field_register_now_text_ms: string;
  field_register_now_text_zh: string;
  field_register_now_button: string;
  field_register_now_button_ms: string;
  field_register_now_button_zh: string;
  filledImage: string;
  webLoginIcon: string;
  webLoginImage: string;
  field_apple_text_en: string;
  field_apple_text_ms: string;
  field_apple_text_zh: string;
  appleImage: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface VMSPopUpResponse {
  id: number;
  name: string;
  field_continue_text_ms: string;
  field_continue_text_zh: string;
  field_vms_text: string;
  field_vms_text_ms: string;
  field_vms_text_zh: string;
  field_vms_button: string;
  field_vms_button_ms: string;
  field_vms_button_zh: string;
}
interface HyperLink {
  id: number;
  name: string;
  field_hyperlink_name_en: string;
  field_hyperlink_name_ms: string;
  field_hyperlink_name_zh: string;
  field_hyperlink_description_en: string;
  field_hyperlink_description_ms: string;
  field_hyperlink_description_zh: string;
  published: boolean;
  field_hyperlink_published: boolean;
  field_hyperlink_created_at: string;
  field_hyperlink_updated_at: string;
  subModule: string;
  epcType: string;
  field_module: string;
  field_sub_module: string;
}

interface WelcomeBanner {
  id: any;
  title: string;
  field_banner_popup_image: string;
  field_web_popup: string;
  field_redirect_url: string;
  field_banner_popup_published: boolean;
  field_banner_popup_created_at: string;
  field_banner_popup_updated_at: string;
  field_image_text_en: string;
  field_image_text_ms: string;
  field_image_text_zh: string;
  field_banner_description_man: string;
  field_banner_description_mal: string;
  field_banner_description_en: string;
  field_description_color: string;
  field_title_color: string;
}

const useCameraAccessPopUp = (
  options?: UseQueryOptions<CameraAccessPopUpResponse[], DefaultError, CameraAccessPopUpResponse[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<CameraAccessPopUpResponse[], DefaultError> => {
  return useQuery<CameraAccessPopUpResponse[], DefaultError, CameraAccessPopUpResponse[], QueryKey>(
    {
      queryKey: ["cameraAccess"],
      enabled: false,
      queryFn: async (): Promise<CameraAccessPopUpResponse[]> => {
        const response = await magpCMSApi.get<CameraAccessPopUpResponse[]>(`/api/get-camera-access-pop-up/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useLanguagePopUp = (
  options?: UseQueryOptions<LanguagePopUpResponse[], DefaultError, LanguagePopUpResponse[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<LanguagePopUpResponse[], DefaultError> => {
  return useQuery<LanguagePopUpResponse[], DefaultError, LanguagePopUpResponse[], QueryKey>(
    {
      queryKey: ["language"],
      enabled: false,
      queryFn: async (): Promise<LanguagePopUpResponse[]> => {
        const response = await magpCMSApi.get<LanguagePopUpResponse[]>(`/api/language-popup/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useLoginPopUp = (
  options?: UseQueryOptions<LoginPageResponse[], DefaultError, LoginPageResponse[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<LoginPageResponse[], DefaultError> => {
  return useQuery<LoginPageResponse[], DefaultError, LoginPageResponse[], QueryKey>(
    {
      queryKey: ["loginPopUp"],
      enabled: false,
      queryFn: async (): Promise<LoginPageResponse[]> => {
        const response = await magpCMSApi.get<LoginPageResponse[]>(`/api/get-login-page/all-fields?id=3`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const usePurchaseLoginPopUp = (
  options?: UseQueryOptions<PurchaseLoginPopUpResponse[], DefaultError, PurchaseLoginPopUpResponse[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PurchaseLoginPopUpResponse[], DefaultError> => {
  return useQuery<PurchaseLoginPopUpResponse[], DefaultError, PurchaseLoginPopUpResponse[], QueryKey>(
    {
      queryKey: ["purchaseLoginPopUp"],
      enabled: false,
      queryFn: async (): Promise<PurchaseLoginPopUpResponse[]> => {
        const response = await magpCMSApi.get<PurchaseLoginPopUpResponse[]>(`/api/purchase-login-popup/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useLogoutPopUp = (
  options?: UseQueryOptions<LogoutPopUpResponse[], DefaultError, LogoutPopUpResponse[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<LogoutPopUpResponse[], DefaultError> => {
  return useQuery<LogoutPopUpResponse[], DefaultError, LogoutPopUpResponse[], QueryKey>(
    {
      queryKey: ["logoutPopUp"],
      enabled: false,
      queryFn: async (): Promise<LogoutPopUpResponse[]> => {
        const response = await magpCMSApi.get<LogoutPopUpResponse[]>(`/api/logout-popup/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useVMSPopUp = (
  options?: UseQueryOptions<VMSPopUpResponse[], DefaultError, VMSPopUpResponse[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<VMSPopUpResponse[], DefaultError> => {
  return useQuery<VMSPopUpResponse[], DefaultError, VMSPopUpResponse[], QueryKey>(
    {
      queryKey: ["VMSPopUp"],
      enabled: false,
      queryFn: async (): Promise<VMSPopUpResponse[]> => {
        const response = await magpCMSApi.get<VMSPopUpResponse[]>(`api/vms-popup/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useHyperLinkPopUp = (
  id: any,
  options?: UseQueryOptions<HyperLink[], DefaultError, HyperLink[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<HyperLink[], DefaultError> => {
  return useQuery<HyperLink[], DefaultError, HyperLink[], QueryKey>(
    {
      queryKey: ["hyperLinkPopUp", id],
      enabled: false,
      queryFn: async (): Promise<HyperLink[]> => {
        const response = await magpCMSApi.get<HyperLink[]>(`/api/hyperlink-popup-details-id/${id}`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWelcomeBanner = (
  options?: UseQueryOptions<WelcomeBanner[], DefaultError, WelcomeBanner[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WelcomeBanner[], DefaultError> => {
  return useQuery<WelcomeBanner[], DefaultError, WelcomeBanner[], QueryKey>(
    {
      queryKey: ["welcomeBanner"],
      enabled: false,
      queryFn: async (): Promise<WelcomeBanner[]> => {
        const response = await magpCMSApi.get<WelcomeBanner[]>(`/api/get-banner-popup/all-fields`);
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export {
  useCameraAccessPopUp,
  useHyperLinkPopUp,
  useLanguagePopUp,
  useLoginPopUp,
  useLogoutPopUp,
  usePurchaseLoginPopUp,
  useVMSPopUp,
  useWelcomeBanner,
};
